import { z } from 'zod'
import {
  type LEGACY_PHOTO_SUPPLIER_IDS,
  type PhotoInput,
  type PhotoMomentType,
  type PhotoRole,
  type PhotoSupplierRow,
  photoRoleCategory,
  photoSchema,
} from './photo'
import type { LegacySupplierLink } from './reception-legacy'
import type { SupplierPlan } from './supplier-plan'
import { weddingPlanSchema } from './wedding-plan'

/** The two-slot plan kept in the wedding plan document before Foto a video had its own plan. */
export const legacyPhotoSchema = weddingPlanSchema.shape.photo
export type LegacyPhoto = z.infer<typeof legacyPhotoSchema>

/** History snapshots hold either shape; a legacy plan has no `moments`. */
export const persistedPhotoSchema = z.union([photoSchema, legacyPhotoSchema])
export type PersistedPhoto = z.infer<typeof persistedPhotoSchema>
export function isLegacyPhoto(plan: PersistedPhoto): plan is LegacyPhoto {
  return !('moments' in plan)
}

/**
 * Text written into converted plans. It becomes stored plan content, like any text the
 * planner typed, so it is persisted once and never re-rendered from this table.
 */
const text = {
  role: { photo: 'Fotograf', video: 'Kameraman' },
  arrival: 'Příjezd ',
  agreement: 'Dohoda: ',
  contact: 'Kontakt: ',
  earlierContact: 'Dříve uvedený kontakt: ',
  moneyPrefix: 'Dříve zadáno v plánu: ',
  moneySuffix: '. Sledujte v Rozpočtu.',
  price: 'cena ',
  paid: 'zaplaceno ',
  currency: ' Kč',
  payment: { cash: 'hotově', card: 'kartou', transfer: 'převodem' },
} as const

/**
 * Names stored on converted moments. They equal the planner's starter labels for the same
 * types.
 */
export const legacyPhotoMomentNames = {
  engagement: 'Zásnubní focení',
} as const satisfies Partial<Record<PhotoMomentType, string>>

export type LegacyPhotoOptions = {
  newId: () => string
  /** Keyed by the legacy supplier id: `photographer` or `video`. */
  links?: Readonly<Record<string, LegacySupplierLink>>
}
/** A named legacy supplier that conversion links to, or creates in, My suppliers. */
export type LegacyPhotoSupplier = {
  id: (typeof LEGACY_PHOTO_SUPPLIER_IDS)[number]
  name: string
  contact: string
  role: PhotoRole
  category: (typeof photoRoleCategory)[PhotoRole]
}

type Slot = {
  id: (typeof LEGACY_PHOTO_SUPPLIER_IDS)[number]
  role: PhotoRole
  supplier: SupplierPlan
  /** Whether the slot becomes a supplier row; otherwise all it held goes into the notes. */
  row: boolean
}

function slots(legacy: LegacyPhoto): Slot[] {
  const slot = (id: Slot['id'], role: PhotoRole, supplier: SupplierPlan, wanted: boolean) => ({
    id,
    role,
    supplier,
    row: wanted && Boolean(supplier.name.trim() || supplier.arrival),
  })
  return [
    slot('photographer', 'photo', legacy.photographer, true),
    // The page did not show a video supplier marked as not needed.
    slot('video', 'video', legacy.video, legacy.videoStatus !== 'unneeded'),
  ]
}

function money(amount: number) {
  return `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}${text.currency}`
}

/** One notes line with what the supplier row cannot hold. */
function supplierLine({ role, supplier, row }: Slot, link: LegacySupplierLink | undefined) {
  const name = supplier.name.trim()
  const agreement = supplier.agreement?.trim()
  const contact = supplier.contact.trim()
  const paid = [
    supplier.amount ? `${text.price}${money(supplier.amount)}` : '',
    supplier.paidAmount ? `${text.paid}${money(supplier.paidAmount)}` : '',
    supplier.paymentMethod && supplier.paymentMethod !== 'undecided'
      ? text.payment[supplier.paymentMethod]
      : '',
  ].filter(Boolean)
  const details = [
    !row && supplier.arrival ? `${text.arrival}${supplier.arrival}` : '',
    agreement ? `${text.agreement}${agreement}` : '',
    contact && !link?.contactStored ? `${link ? text.earlierContact : text.contact}${contact}` : '',
    paid.length ? `${text.moneyPrefix}${paid.join(', ')}${text.moneySuffix}` : '',
  ].filter(Boolean)
  // Without a row, the name itself would otherwise be lost.
  if (!details.length && (row || !name)) return ''
  const who = name ? `${text.role[role]} ${name}` : text.role[role]
  return details.length ? `${who}: ${details.join(' · ')}` : who
}

/**
 * Turns a legacy plan into supplier rows and moments without losing what was entered. Rows
 * keep the legacy ids so Harmonogram arrival events and overrides survive; money, agreements
 * and contacts My suppliers does not hold become page notes.
 */
export function legacyPhotoToPlan(
  legacy: LegacyPhoto,
  { newId, links = {} }: LegacyPhotoOptions,
): PhotoInput {
  const suppliers: PhotoSupplierRow[] = []
  const notes: string[] = []
  for (const slot of slots(legacy)) {
    const link = slot.row ? links[slot.id] : undefined
    if (slot.row)
      suppliers.push({
        id: slot.id,
        ...(link ? { weddingSupplierId: link.weddingSupplierId } : {}),
        name: slot.supplier.name.trim(),
        role: slot.role,
        purpose: '',
        arrival: slot.supplier.arrival ?? '',
        end: '',
      })
    const line = supplierLine(slot, link)
    if (line) notes.push(line)
  }
  const moments: PhotoInput['moments'] = legacy.engagement
    ? [
        {
          id: newId(),
          type: 'engagement',
          name: legacyPhotoMomentNames.engagement,
          scope: 'other',
          date: '',
          time: '',
          timeEnd: '',
          place: '',
          coveredBy: [],
          guestVisible: false,
          followsCeremony: false,
          notes: '',
        },
      ]
    : []
  return {
    suppliers,
    videoUnneeded: legacy.videoStatus === 'unneeded',
    moments,
    groups: [],
    wishes: [],
    notes: notes.join('\n'),
    hiddenPromptCodes: [],
  }
}

/** Named legacy suppliers that conversion should link to, or create in, My suppliers. */
export function legacyPhotoSuppliers(legacy: LegacyPhoto): LegacyPhotoSupplier[] {
  return slots(legacy)
    .filter((slot) => slot.row && slot.supplier.name.trim())
    .map(({ id, role, supplier }) => ({
      id,
      name: supplier.name.trim(),
      contact: supplier.contact.trim(),
      role,
      category: photoRoleCategory[role],
    }))
}
